/** Sign in or sign up, one act. */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, ErrorText, Field, Input } from '@metal/ui'
import { ApiError, api } from '@metal/ui/api'
import { useMarketClock, useSession } from '@metal/ui/session'
import { isMobileShaped, mmss, OtpPanel, type OtpChallenge } from '@metal/ui/otp'

interface VerifyAnswer {
  accessToken?: string
  registrationTicket?: string
  needsName?: boolean
}

interface ConsentDocument {
  documentKey: string
  titleFa: string
  version: string
  bodyFa: string
}

export interface AuthPanelProps {
  /** Called once the session is set. `signedUp` is true for a new account. */
  onDone: (result: { signedUp: boolean }) => void
  /** The page draws its own heading; the modal has one in its frame. */
  heading?: string
  /** A line the caller was sent here with, drawn above the number. */
  notice?: string | null
  initialMobile?: string
}

type Phase = 'phone' | 'code' | 'name'

/** A document title that opens its text in place, drawn as a link. */
const DOC_LINK = {
  background: 'none',
  border: 0,
  padding: 0,
  font: 'inherit',
  color: 'var(--c-accent)',
  textDecoration: 'underline',
  cursor: 'pointer',
} as const

export function AuthPanel({ onDone, heading, notice, initialMobile = '' }: AuthPanelProps) {
  const { setAccessToken } = useSession()
  const clock = useMarketClock()
  const nowMs = clock.now.getTime()

  const [phase, setPhase] = useState<Phase>('phone')
  const [mobile, setMobile] = useState(initialMobile)
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null)
  const [ticket, setTicket] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [failure, setFailure] = useState<ApiError | null>(null)
  const [staffNumber, setStaffNumber] = useState(false)
  const [busy, setBusy] = useState(false)
  const [throttledUntil, setThrottledUntil] = useState<number | null>(null)
  const [openDoc, setOpenDoc] = useState<string | null>(null)

  const throttledFor =
    throttledUntil === null ? 0 : Math.max(0, Math.ceil((throttledUntil - nowMs) / 1000))
  const shaped = isMobileShaped(mobile)

  // The two documents a new account accepts, at the version published now.
  const docs = useQuery({
    queryKey: ['consent-documents', 2],
    queryFn: () => api.get<{ items: ConsentDocument[] }>('/consents/documents', { step: 2 }),
    enabled: phase === 'name',
  })

  const request = async (): Promise<OtpChallenge> =>
    api.post<OtpChallenge>(
      '/auth/otp/request',
      { mobile, purpose: 'LOGIN', signUp: true },
      { raw: true },
    )

  const start = async () => {
    if (!shaped || busy || throttledFor > 0) return
    setBusy(true)
    setFailure(null)
    try {
      setChallenge(await request())
      setPhase('code')
    } catch (e) {
      if (e instanceof ApiError) {
        setFailure(e)
        if (e.retryAfterSeconds !== null) setThrottledUntil(nowMs + e.retryAfterSeconds * 1000)
      }
    } finally {
      setBusy(false)
    }
  }

  const verify = async (code: string, challengeId: string) => {
    try {
      const done = await api.post<VerifyAnswer>(
        '/auth/otp/verify',
        { challengeId, code },
        { raw: true },
      )
      if (done.accessToken) {
        await setAccessToken(done.accessToken)
        onDone({ signedUp: false })
        return
      }
      if (done.registrationTicket) {
        setTicket(done.registrationTicket)
        setPhase('name')
      }
    } catch (e) {
      // A staff number:
      if (e instanceof ApiError && e.is('MFA_REQUIRED')) {
        setChallenge(null)
        setPhase('phone')
        setStaffNumber(true)
        return
      }
      throw e
    }
  }

  const create = async () => {
    if (!ticket || name.trim().length < 2 || busy) return
    setBusy(true)
    setFailure(null)
    try {
      const items = docs.data?.items ?? []
      const created = await api.post<{ accessToken?: string }>(
        '/registration/basic',
        {
          name: name.trim(),
          mobile,
          registrationTicket: ticket,
          consents: items.map((d) => ({
            documentKey: d.documentKey,
            version: d.version,
            accepted: true,
          })),
        },
        { raw: true },
      )
      if (created.accessToken) {
        await setAccessToken(created.accessToken)
        onDone({ signedUp: true })
      }
    } catch (e) {
      if (e instanceof ApiError) {
        // A spent or stale ticket means starting the number again.
        if (e.code.startsWith('REGISTRATION_TICKET')) {
          setTicket(null)
          setPhase('phone')
        }
        setFailure(e)
      }
    } finally {
      setBusy(false)
    }
  }

  if (phase === 'code' && challenge) {
    return (
      <OtpPanel
        challenge={challenge}
        onVerify={verify}
        onResend={request}
        onEdit={() => {
          setChallenge(null)
          setPhase('phone')
        }}
        submitLabel="ادامه"
      />
    )
  }

  if (phase === 'name') {
    const items = docs.data?.items ?? []
    const shown = items.find((d) => d.documentKey === openDoc)
    return (
      <div className="col" style={{ gap: 20 }}>
        <h2 className="t-h2">ساخت حساب</h2>

        <Field
          label="نام و نام خانوادگی"
          required
          error={failure?.fields?.name ?? (failure && !failure.fields ? failure.message : undefined)}
        >
          <Input
            autoFocus
            value={name}
            invalid={Boolean(failure)}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void create()}
          />
        </Field>

        <p className="t-sm t-sec" style={{ lineHeight: 1.9 }}>
          با ایجاد حساب،{' '}
          {items.map((d, i) => (
            <span key={d.documentKey}>
              {i > 0 && ' و '}
              <button
                type="button"
                style={DOC_LINK}
                onClick={() => setOpenDoc((k) => (k === d.documentKey ? null : d.documentKey))}
              >
                {d.titleFa}
              </button>
            </span>
          ))}{' '}
          را می‌پذیرید.
        </p>

        {shown && (
          <div
            style={{
              background: 'var(--p-surface-2)',
              borderRadius: 'var(--r-md)',
              padding: '14px 16px',
            }}
          >
            <p className="t-sm t-sec" style={{ lineHeight: 2, whiteSpace: 'pre-line' }}>
              {shown.bodyFa}
            </p>
          </div>
        )}

        <Button
          variant="primary"
          size="lg"
          block
          loading={busy}
          disabled={name.trim().length < 2 || docs.isLoading}
          onClick={() => void create()}
        >
          ساخت حساب و ورود
        </Button>
      </div>
    )
  }

  return (
    <div className="col" style={{ gap: 22 }}>
      {heading && <h1 className="t-h1">{heading}</h1>}

      {notice && (
        <p className="t-sm t-sec" role="status">
          {notice}
        </p>
      )}

      <div className="col" style={{ gap: 16 }}>
        <Field label="شماره همراه" required>
          <Input
            numeric
            inputMode="numeric"
            autoFocus
            dir="ltr"
            value={mobile}
            invalid={mobile.length > 0 && !shaped}
            aria-label="شماره همراه"
            onChange={(e) => {
              setMobile(e.target.value.replace(/\D/g, '').slice(0, 11))
              setStaffNumber(false)
            }}
            onKeyDown={(e) => e.key === 'Enter' && void start()}
          />
        </Field>

        {throttledFor > 0 ? (
          <div className="row-b">
            <span className="t-sm t-muted" role="status">
              {failure?.message}
            </span>
            <span className="t-sm t-muted num">{mmss(throttledFor)}</span>
          </div>
        ) : (
          failure && <ErrorText>{failure.message}</ErrorText>
        )}
        {staffNumber && <ErrorText>این شماره متعلق به مدیریت است و از پنل اپراتور وارد می‌شود.</ErrorText>}
      </div>

      <Button
        variant="primary"
        size="lg"
        block
        loading={busy}
        disabled={!shaped || throttledFor > 0}
        onClick={() => void start()}
      >
        دریافت کد
      </Button>
    </div>
  )
}
